import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from 'antd';

import Pficon from 'components/Pficon';
import { SETTINGS_TOOLTIP_TYPES } from 'components/NodeHeaderSettings/constants';
import { QA_ACTION_TYPES } from 'containers/QuestionnairePage/constants';

import ConfirmModal from './ConfirmModal';
import HideButton from './HideButton';
import RestoreButton from './RestoreButton';
import useTooltipTitle from './hooks/useTooltipTitle';
import useStatePropsAndActions from './hooks/useStatePropsAndActions';
import { HIDE_ANSWER_LOGIC_MODE } from './constants';

const UnconditionalHideAnswer = ({
  nodeRefId,
  valueRefId,
  valueType,
  type = QA_ACTION_TYPES.BUTTON,
}) => {
  const isListItem = type === QA_ACTION_TYPES.BUTTON_WITH_TEXT;
  const {
    hasLogic,
    hasHideLogic,
    actionPayload,
    quotaModeToBeCleared,
    insideMatrix,
    insideNewMatrix,
    canUnconditionalHideAnswer,
    canUnconditionalHideAnswerReset,
    onAddUnconditionalHideAnswer,
    onRemoveUnconditionalHideAnswer,
  } = useStatePropsAndActions({ nodeRefId, valueRefId, valueType });

  const hideLabel = useTooltipTitle(
    valueType,
    undefined,
    SETTINGS_TOOLTIP_TYPES.DELETE,
  );
  const restoreLabel = useTooltipTitle(
    valueType,
    undefined,
    SETTINGS_TOOLTIP_TYPES.RESTORE,
  );

  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  const onClickHideAnswer = useCallback(() => {
    if (!hasLogic) {
      onAddUnconditionalHideAnswer({
        ...actionPayload,
        hideLogicMode: HIDE_ANSWER_LOGIC_MODE.REMOVE_RULES,
      });
      return;
    }

    setShowConfirmModal(true);
  }, [hasLogic, actionPayload, onAddUnconditionalHideAnswer]);

  const onHideAndRemoveRules = useCallback(() => {
    onAddUnconditionalHideAnswer({
      ...actionPayload,
      hideLogicMode: HIDE_ANSWER_LOGIC_MODE.REMOVE_RULES,
    });
    setShowConfirmModal(false);
  }, [actionPayload, onAddUnconditionalHideAnswer]);

  const onHideAnswerOnly = useCallback(() => {
    onAddUnconditionalHideAnswer({
      ...actionPayload,
      hideLogicMode: HIDE_ANSWER_LOGIC_MODE.KEEP_RULES,
    });
    setShowConfirmModal(false);
  }, [actionPayload, onAddUnconditionalHideAnswer]);

  const onCloseConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  const confirmModalElement = showConfirmModal ? (
    <ConfirmModal
      valueType={valueType}
      quotaModeToBeCleared={quotaModeToBeCleared}
      onCancel={onCloseConfirmModal}
      onSubmit={onHideAndRemoveRules}
      onHideAnswerOnly={onHideAnswerOnly}
    />
  ) : null;

  if (isListItem) {
    if (hasHideLogic) {
      return (
        <Fragment>
          {confirmModalElement}
          <Button
            tabIndex="-1"
            className={classNames(
              'text-align-left',
              'border-none',
              'padding-horizontal-xxs',
              'shadow-none',
              'background-transparent',
              'background-transparent-hover',
              'w-full',
              {
                'color-blue-6-hover': canUnconditionalHideAnswer,
                'color-grey-7 color-grey-7-hover': !canUnconditionalHideAnswer,
              },
            )}
            disabled={!canUnconditionalHideAnswer}
            onClick={() => onRemoveUnconditionalHideAnswer(actionPayload)}
          >
            <Pficon
              className={classNames('text-size-secondary', 'margin-right-xxs', {
                'color-grey-7': !canUnconditionalHideAnswer,
              })}
              type="restart"
            />
            {restoreLabel}
          </Button>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {confirmModalElement}
        <Button
          tabIndex="-1"
          className={classNames(
            'text-align-left',
            'border-none',
            'padding-horizontal-xxs',
            'shadow-none',
            'background-transparent',
            'background-transparent-hover',
            'w-full',
            {
              'color-blue-6-hover': canUnconditionalHideAnswerReset,
              'color-grey-7 color-grey-7-hover':
                !canUnconditionalHideAnswerReset,
            },
          )}
          disabled={!canUnconditionalHideAnswerReset}
          onClick={onClickHideAnswer}
        >
          <Pficon
            className={classNames('text-size-secondary', 'margin-right-xxs', {
              'color-grey-7': !canUnconditionalHideAnswerReset,
            })}
            type="close"
          />
          {hideLabel}
        </Button>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {confirmModalElement}
      {hasHideLogic ? (
        <RestoreButton
          valueType={valueType}
          isDisabled={!canUnconditionalHideAnswer}
          insideMatrix={insideMatrix}
          action={() => onRemoveUnconditionalHideAnswer(actionPayload)}
        />
      ) : (
        <HideButton
          valueType={valueType}
          isDisabled={!canUnconditionalHideAnswerReset}
          insideMatrix={insideMatrix}
          insideNewMatrix={insideNewMatrix}
          action={onClickHideAnswer}
        />
      )}
    </Fragment>
  );
};

UnconditionalHideAnswer.propTypes = {
  nodeRefId: PropTypes.string.isRequired,
  valueRefId: PropTypes.string.isRequired,
  valueType: PropTypes.string.isRequired,
  type: PropTypes.string,
};

export default React.memo(UnconditionalHideAnswer);
